<script setup lang="ts">
	import { getRoleClasses } from '@/utils/roleColors'
	import { Float } from '@headlessui-float/vue'
	import {
		Menu,
		MenuButton,
		MenuItem,
		MenuItems,
		TabGroup,
		TabPanel,
		TabPanels,
	} from '@headlessui/vue'
	import {
		EllipsisVerticalIcon,
		EnvelopeIcon,
		PencilSquareIcon,
		UserIcon,
	} from '@heroicons/vue/20/solid'
	import { Icon } from '@iconify/vue'
	import moment from 'moment'
	import { ref } from 'vue'

	import ModalComponent from '@/components/ModalComponent.vue'
	import ShadowBox from '@/components/ShadowBox.vue'
	import StyledTabNavbar from '@/components/StyledTabNavbar.vue'

	import useAccount from '@/composables/useAccount'
	import useAuthUser from '@/composables/useAuthUser'
	import useHasPermissionUser from '@/composables/useHasPermissionUser'

	import useCompanies from '@/modules/companies/composables/useCompanies'
	import { Company } from '@/modules/companies/interfaces/company'
	import InviteUserForm from '@/modules/users/components/InviteUserForm.vue'
	import InviteUserMassive from '@/modules/users/components/InviteUserMassive.vue'
	import { User } from '@/modules/users/interfaces/user'

	type buttonList = {
		name: string
		iconName: string
		href?: string
		action?: () => void
	}

	interface Props {
		isTab?: boolean
		users: User[]
		pagination: any
		page: any
		companyName?: string
		companyRequest?: Company
		buttonsList?: buttonList[]
		internal?: boolean
		permission?: string
		model?: string
		toShowUser: {
			name: string
		}
		toEditUser: {
			name: string
		}
		toPermissionsUser: {
			name: string
		}
	}

	interface Emits {
		(event: 'update:changePage', value: string): void
		(event: 'refetch'): Promise<void>
		(event: 'deactivateUser', id: string): Promise<void>
		(event: 'reactivateUser', id: string): Promise<void>
	}

	const modalInviteUser = ref(false)

	function actionModalInviteUser() {
		modalInviteUser.value = !modalInviteUser.value
	}

	const props = defineProps<Props>()

	const emits = defineEmits<Emits>()
	const { user: userLogged } = useAuthUser()
	const { hasPermissionUser } = useHasPermissionUser()
	const { companies } = useCompanies({
		errorHandling: false,
	})

	const PermissionUser = {
		CreateUsersCompanies:
			!userLogged.value?.internal ? 'create-users' : 'create-internal',
		ViewUserCompanies:
			!userLogged.value?.internal ? 'view-users' : 'view-internal',
		UpdateUsersCompanies:
			!userLogged.value?.internal ? 'update-users' : 'update-internal',
		RestoreUsersCompanies:
			!userLogged.value?.internal ? 'restore-users' : 'restore-internal',
		DeleteUsersCompanies:
			!userLogged.value?.internal ? 'delete-users' : 'delete-internal',
	}

	const EntityType = {
		CompanyType:
			!userLogged.value?.internal ?
				'App\\Models\\Company'
			:	'App\\Models\\User',
	}

	const { resendVerificationEmail } = useAccount()

	const handleSendVerifyEmail = async (id: string) => {
		await resendVerificationEmail({
			params: { id },
			config: {
				raiseToast: true,
				onSuccessToast: 'Se ha enviado un correo de verificación al usuario.',
			},
		})
	}

	// Función para calcular las páginas visibles en la paginación
	const getVisiblePages = () => {
		if (!props.pagination?.meta) return []

		const currentPage = props.pagination.meta.currentPage
		const lastPage = props.pagination.meta.lastPage
		const maxVisiblePages = 5

		if (lastPage <= maxVisiblePages) {
			return Array.from({ length: lastPage }, (_, i) => i + 1)
		}

		const pages: (number | string)[] = []

		if (currentPage <= 3) {
			// Mostrar las primeras páginas
			for (let i = 1; i <= Math.min(4, lastPage); i++) {
				pages.push(i)
			}
			if (lastPage > 4) {
				pages.push('...')
				pages.push(lastPage)
			}
		} else if (currentPage >= lastPage - 2) {
			// Mostrar las últimas páginas
			pages.push(1)
			if (lastPage > 4) {
				pages.push('...')
			}
			for (let i = Math.max(lastPage - 3, 1); i <= lastPage; i++) {
				pages.push(i)
			}
		} else {
			// Mostrar páginas del medio
			pages.push(1)
			pages.push('...')
			for (let i = currentPage - 1; i <= currentPage + 1; i++) {
				pages.push(i)
			}
			pages.push('...')
			pages.push(lastPage)
		}

		return pages
	}

	const changePage = (page: number) => {
		emits('update:changePage', page.toString())
	}

	// Helper to get user status for UI
	const getStatus = (deletedAt: string | null | undefined) => {
		if (!deletedAt) {
			return {
				value: {
					background: 'bg-emerald-50 border-emerald-200',
					text: 'text-emerald-700',
					icon: 'mdi:check-circle',
					message: 'Activo',
				},
			}
		}
		return {
			value: {
				background: 'bg-gray-100 border-gray-200',
				text: 'text-gray-500',
				icon: 'mdi:close-circle',
				message: 'Inactivo',
			},
		}
	}
</script>

<template>
	<div class="w-full">
		<div class="flex w-full flex-col justify-between">
			<div class="">
				<slot name="filters" />
			</div>

			<ModalComponent
				:model-value="modalInviteUser"
				@update:model-value="actionModalInviteUser"
			>
				<template #modalBody>
					<ShadowBox class="bg-white">
						<TabGroup>
							<StyledTabNavbar
								:tabs="[
									'Invitación Individual',
									'Invitación a Varios Usuarios',
								]"
							/>

							<TabPanels>
								<!-- Invite user form -->
								<TabPanel>
									<InviteUserForm
										:companies="companies"
										:user-logged="userLogged"
										@action-modal-invite-user="actionModalInviteUser"
									/>
								</TabPanel>

								<!-- Massive invitation -->
								<TabPanel>
									<InviteUserMassive
										:user-logged="userLogged"
										:companies="companies"
										@action-modal-invite-user="actionModalInviteUser"
									/>
								</TabPanel>
							</TabPanels>
						</TabGroup>
					</ShadowBox>
				</template>
			</ModalComponent>
		</div>

		<!-- Información de resultados -->
		<template v-if="users && users.length > 0 && pagination?.meta?.total">
			<div
				class="flex items-center gap-2 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 mb-0.5"
			>
				<svg
					class="w-4 h-4 text-gray-400 flex-shrink-0"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
					></path>
				</svg>
				<span class="font-medium"
					>{{ pagination.meta.total }} usuarios encontrados</span
				>
				<div class="hidden sm:flex items-center gap-2 text-gray-400">
					<span>•</span>
					<span
						>Mostrando {{ pagination.meta.from }} -
						{{ pagination.meta.to }}</span
					>
				</div>
			</div>
		</template>

		<div class="w-full">
			<!-- Vista Móvil Empresarial -->
			<section class="lg:hidden w-full relative">
				<div class="space-y-4">
					<!-- Contenido móvil cuando hay datos -->
					<template v-if="users && users.length > 0">
						<div
							v-for="user in users"
							:key="user.id"
							:class="[
								'bg-gradient-to-br from-slate-50 to-gray-100/50 rounded-2xl shadow-sm border border-gray-200/60 hover:shadow-lg hover:border-gray-300/60 transition-all duration-300 overflow-hidden backdrop-blur-sm',
								!user.deletedAt ?
									'from-slate-50 to-gray-100/50'
								:	'from-gray-100 to-gray-200/50 opacity-80',
							]"
						>
							<!-- Header principal con información del usuario -->
							<div class="p-4">
								<div class="flex items-start gap-4">
									<!-- Avatar con indicador de estado -->
									<div class="relative flex-shrink-0">
										<img
											:src="user.avatar"
											:alt="`Avatar de ${user.name}`"
											class="w-16 h-16 rounded-2xl shadow-md border-2 border-white object-cover"
										/>
									</div>

									<!-- Información principal del usuario -->
									<div class="flex-1 min-w-0 space-y-2">
										<!-- Nombre -->
										<div>
											<RouterLink
												v-if="
													hasPermissionUser(
														PermissionUser.ViewUserCompanies,
														EntityType.CompanyType
													)
												"
												:to="{
													name: toShowUser.name,
													params: { id: user.id },
												}"
												class="text-base font-bold text-gray-900 hover:text-blue-600 transition-colors line-clamp-2"
											>
												{{
													`${user.name} ${user.secondName ?? ''} ${user.lastname} ${user.secondLastname ?? ''}`
												}}
											</RouterLink>
											<h3
												v-else
												class="text-base font-bold text-gray-900 line-clamp-2"
											>
												{{
													`${user.name} ${user.secondName ?? ''} ${user.lastname} ${user.secondLastname ?? ''}`
												}}
											</h3>
										</div>

										<!-- Email con badge de verificación debajo -->
										<div class="space-y-1.5">
											<div
												class="flex items-center gap-2 text-sm text-gray-600"
											>
												<EnvelopeIcon
													class="w-4 h-4 text-gray-400 flex-shrink-0"
												/>
												<span class="truncate">{{
													user.email || 'Sin email'
												}}</span>
											</div>

											<!-- Badge de verificación de correo -->
											<div class="flex items-center gap-2 pl-6">
												<span
													:class="[
														'inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium',
														user.emailVerifiedAt ?
															'bg-emerald-50 text-emerald-700 border border-emerald-200'
														: user.email ?
															'bg-amber-50 text-amber-700 border border-amber-200'
														:	'bg-gray-50 text-gray-600 border border-gray-200',
													]"
												>
													{{
														user.emailVerifiedAt ? '✓ Verificado'
														: user.email ? 'Pendiente de verificar'
														: 'Sin correo'
													}}
												</span>
											</div>
										</div>

										<!-- Estado de activo/inactivo -->
										<div class="flex items-center gap-2">
											<!-- Badge de estado activo/inactivo -->
											<span
												:class="[
													'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium shadow-sm',
													getStatus(user.deletedAt).value.background,
													getStatus(user.deletedAt).value.text,
												]"
											>
												<Icon
													:icon="getStatus(user.deletedAt).value.icon"
													class="w-3.5 h-3.5"
												/>
												{{ getStatus(user.deletedAt).value.message }}
											</span>
										</div>
									</div>
									<!-- Menú de acciones -->
									<Menu
										as="div"
										class="relative flex-shrink-0"
										v-if="
											hasPermissionUser(
												PermissionUser.UpdateUsersCompanies,
												EntityType.CompanyType
											) ||
											hasPermissionUser(
												PermissionUser.DeleteUsersCompanies,
												EntityType.CompanyType
											) ||
											(hasPermissionUser(
												PermissionUser.RestoreUsersCompanies,
												EntityType.CompanyType
											) &&
												user.deletedAt)
										"
									>
										<Float
											strategy="fixed"
											placement="bottom-end"
											:offset="4"
											enter="transition ease-out duration-100"
											enter-from="transform opacity-0 scale-95"
											enter-to="transform opacity-100 scale-100"
											leave="transition ease-in duration-75"
											leave-from="transform opacity-100 scale-100"
											leave-to="transform opacity-0 scale-95"
										>
											<MenuButton
												class="p-2 rounded-xl bg-white/80 hover:bg-white border border-gray-200 shadow-sm transition-all duration-200"
											>
												<EllipsisVerticalIcon class="w-5 h-5 text-gray-600" />
											</MenuButton>

											<MenuItems
												class="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-xl bg-white shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none border border-gray-200 z-50"
											>
												<div class="p-1">
													<MenuItem
														v-if="
															!user.deletedAt &&
															hasPermissionUser(
																PermissionUser.UpdateUsersCompanies,
																EntityType.CompanyType
															)
														"
														v-slot="{ active }"
													>
														<RouterLink
															:to="{
																name: toEditUser.name,
																params: { id: user.id },
															}"
															:class="[
																'flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors',
																active ?
																	'bg-blue-50 text-blue-700'
																:	'text-gray-700',
															]"
														>
															<PencilSquareIcon class="w-5 h-5" />
															Editar usuario
														</RouterLink>
													</MenuItem>

													<MenuItem
														v-if="
															!user.deletedAt &&
															hasPermissionUser(
																PermissionUser.UpdateUsersCompanies,
																EntityType.CompanyType
															)
														"
														v-slot="{ active }"
													>
														<RouterLink
															:to="{
																name: toPermissionsUser.name,
																params: { id: user.id },
															}"
															:class="[
																'flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors',
																active ?
																	'bg-purple-50 text-purple-700'
																:	'text-gray-700',
															]"
														>
															<Icon
																icon="mdi:shield-key"
																class="w-5 h-5"
															/>
															Roles y permisos
														</RouterLink>
													</MenuItem>

													<MenuItem
														v-if="
															!user.deletedAt &&
															user.email &&
															!user.emailVerifiedAt &&
															hasPermissionUser(
																PermissionUser.UpdateUsersCompanies,
																EntityType.CompanyType
															)
														"
														v-slot="{ active }"
													>
														<button
															@click.stop.prevent="
																handleSendVerifyEmail(user.id.toString())
															"
															:class="[
																'w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors',
																active ?
																	'bg-blue-50 text-blue-700'
																:	'text-gray-700',
															]"
														>
															<EnvelopeIcon class="w-5 h-5" />
															Verificar correo
														</button>
													</MenuItem>

													<MenuItem
														v-if="
															!user.deletedAt &&
															hasPermissionUser(
																PermissionUser.DeleteUsersCompanies,
																EntityType.CompanyType
															)
														"
														v-slot="{ active }"
													>
														<button
															@click.stop.prevent="
																emits('deactivateUser', user.id.toString())
															"
															:class="[
																'w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors',
																active ?
																	'bg-red-50 text-red-700'
																:	'text-gray-700',
															]"
														>
															<Icon
																icon="mdi:account-off"
																class="w-5 h-5"
															/>
															Desactivar usuario
														</button>
													</MenuItem>

													<MenuItem
														v-if="
															user.deletedAt &&
															hasPermissionUser(
																PermissionUser.RestoreUsersCompanies,
																EntityType.CompanyType
															)
														"
														v-slot="{ active }"
													>
														<button
															@click.stop.prevent="
																emits('reactivateUser', user.id.toString())
															"
															:class="[
																'w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors',
																active ?
																	'bg-green-50 text-green-700'
																:	'text-gray-700',
															]"
														>
															<Icon
																icon="mdi:account-check"
																class="w-5 h-5"
															/>
															Reactivar usuario
														</button>
													</MenuItem>
												</div>
											</MenuItems>
										</Float>
									</Menu>
								</div>
							</div>

							<!-- Separador sutil -->
							<div
								class="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mx-4"
							></div>

							<!-- Información organizada en cards compactas -->
							<div class="p-4 space-y-3">
								<!-- Roles del sistema -->
								<div
									class="bg-white/40 backdrop-blur-sm rounded-xl p-3 border border-blue-200/30 shadow-sm"
								>
									<div class="flex items-center gap-2 mb-2">
										<Icon
											icon="mdi:shield-account"
											class="w-4 h-4 text-blue-600"
										/>
										<h4 class="text-xs font-semibold text-gray-700">
											Roles del Sistema
										</h4>
									</div>
									<div class="flex flex-wrap gap-1.5">
										<template v-if="user.roles && user.roles.length > 0">
											<span
												v-for="(role, i) in user.roles"
												:key="i"
												:class="[
													'inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium shadow-sm',
													getRoleClasses(role),
												]"
											>
												{{ role }}
											</span>
										</template>
										<span
											v-else
											class="text-xs text-gray-400 italic"
										>
											Sin roles asignados
										</span>
									</div>
								</div>
							</div>
						</div>
					</template>

					<!-- Mensaje cuando no hay datos en móvil -->
					<template v-else>
						<div
							class="bg-gradient-to-br from-slate-50 to-gray-100/50 rounded-2xl border border-gray-200/60 shadow-sm p-8 text-center backdrop-blur-sm"
						>
							<div class="max-w-sm mx-auto">
								<Icon
									icon="mdi:account-off-outline"
									class="w-16 h-16 mx-auto text-gray-300 mb-4"
								/>
								<h3 class="text-lg font-semibold text-gray-900 mb-2">
									No hay usuarios
								</h3>
								<p class="text-sm text-gray-500">
									No se encontraron usuarios que coincidan con los filtros
									aplicados.
								</p>
							</div>
						</div>
					</template>
				</div>
			</section>

			<!-- Vista Desktop (tabla mejorada) -->
			<section class="hidden lg:block w-full">
				<div
					class="w-full bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
				>
					<!-- Header de la tabla -->
					<div class="bg-gray-50 border-b border-gray-200 px-6 py-4">
						<div
							class="grid grid-cols-12 gap-4 items-center text-sm font-semibold text-gray-700"
						>
							<div class="col-span-1 flex justify-center">
								<UserIcon class="w-5 h-5 text-gray-400" />
							</div>
							<div class="col-span-3">
								<span>Usuario</span>
							</div>
							<div class="col-span-2 text-center">
								<span>Roles</span>
							</div>
							<div class="col-span-2 text-center">
								<span>Verificación</span>
							</div>
							<div class="col-span-2 text-center">
								<span>Estatus</span>
							</div>
							<div class="col-span-1 text-center">
								<span>Fecha</span>
							</div>
							<div class="col-span-1 text-center">
								<span>Acciones</span>
							</div>
						</div>
					</div>

					<!-- Contenido de la tabla -->
					<div class="divide-y divide-gray-200">
						<template v-if="users && users.length > 0">
							<div
								v-for="user in users"
								:key="user.id"
								:class="[
									'grid grid-cols-12 gap-4 items-center px-6 py-4 hover:bg-gray-50 transition-colors duration-150',
									user.deletedAt && 'bg-gray-50 opacity-70',
								]"
							>
								<!-- Avatar -->
								<div class="col-span-1 flex justify-center">
									<div class="relative">
										<img
											:src="user.avatar"
											:alt="`Avatar de ${user.name}`"
											class="w-10 h-10 rounded-full border-2 border-white shadow-sm"
										/>
									</div>
								</div>

								<!-- Usuario info -->
								<div class="col-span-3">
									<RouterLink
										v-if="
											hasPermissionUser(
												PermissionUser.ViewUserCompanies,
												EntityType.CompanyType
											)
										"
										:to="{
											name: toShowUser.name,
											params: { id: user.id },
										}"
										class="block group"
									>
										<p
											class="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors truncate"
										>
											{{
												`${user.name} ${user.secondName ?? ''} ${user.lastname} ${user.secondLastname ?? ''}`
											}}
										</p>
										<p
											class="text-xs text-gray-500 truncate flex items-center gap-1 mt-0.5"
										>
											<EnvelopeIcon class="w-3.5 h-3.5" />
											{{ user.email || 'Sin email' }}
										</p>
									</RouterLink>
									<div v-else>
										<p class="text-sm font-semibold text-gray-900 truncate">
											{{
												`${user.name} ${user.secondName ?? ''} ${user.lastname} ${user.secondLastname ?? ''}`
											}}
										</p>
										<p
											class="text-xs text-gray-500 truncate flex items-center gap-1 mt-0.5"
										>
											<EnvelopeIcon class="w-3.5 h-3.5" />
											{{ user.email || 'Sin email' }}
										</p>
									</div>
								</div>

								<!-- Roles -->
								<div class="col-span-2">
									<div class="flex flex-wrap gap-1 justify-center">
										<template v-if="user.roles && user.roles.length > 0">
											<span
												v-for="(role, i) in user.roles.slice(0, 2)"
												:key="i"
												:class="[
													'inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium',
													getRoleClasses(role),
												]"
											>
												{{ role }}
											</span>
											<span
												v-if="user.roles.length > 2"
												class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600"
											>
												+{{ user.roles.length - 2 }}
											</span>
										</template>
										<span
											v-else
											class="text-xs text-gray-400 italic"
										>
											Sin roles
										</span>
									</div>
								</div>

								<!-- Verificación -->
								<div class="col-span-2 flex justify-center">
									<VDropdown
										:distance="6"
										placement="top"
									>
										<span
											:class="[
												'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium cursor-pointer shadow-sm transition-all',
												user.emailVerifiedAt ?
													'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
												: user.email ?
													'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
												:	'bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100',
											]"
										>
											<Icon
												:icon="
													user.emailVerifiedAt ? 'mdi:check-circle' : (
														'mdi:alert-circle'
													)
												"
												class="w-3.5 h-3.5"
											/>
											{{
												user.emailVerifiedAt ? 'Verificado'
												: user.email ? 'Pendiente'
												: 'No tiene correo'
											}}
										</span>

										<template #popper>
											<div
												class="bg-white rounded-xl shadow-xl border border-gray-200 p-4 max-w-xs"
											>
												<template v-if="user.emailVerifiedAt">
													<div class="space-y-2">
														<h4 class="text-sm font-semibold text-gray-900">
															✓ Usuario verificado
														</h4>
														<p class="text-xs text-gray-600">
															Correo confirmado el
															<span class="font-medium text-gray-900">{{
																moment(user.emailVerifiedAt).format('LL')
															}}</span>
														</p>
													</div>
												</template>
												<template v-else>
													<div class="space-y-3">
														<h4 class="text-sm font-semibold text-gray-900">
															Correo sin verificar
														</h4>
														<p class="text-xs text-gray-600">
															Este usuario aún no ha confirmado su dirección de
															correo.
														</p>
														<button
															v-if="user.email && !user.deletedAt"
															@click.prevent="
																handleSendVerifyEmail(user.id.toString())
															"
															class="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
														>
															<EnvelopeIcon class="w-4 h-4" />
															Reenviar invitación
														</button>
													</div>
												</template>
											</div>
										</template>
									</VDropdown>
								</div>

								<!-- Estado -->
								<div class="col-span-2 flex justify-center">
									<span
										:class="[
											'inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium shadow-sm',
											getStatus(user.deletedAt).value.background,
											getStatus(user.deletedAt).value.text,
										]"
									>
										<Icon
											:icon="getStatus(user.deletedAt).value.icon"
											class="w-3.5 h-3.5"
										/>
										{{ getStatus(user.deletedAt).value.message }}
									</span>
								</div>

								<!-- Fecha de creación -->
								<div class="col-span-1 text-center">
									<p class="text-xs text-gray-500">
										{{ moment(user.createdAt).format('DD/MM/YY') }}
									</p>
								</div>

								<!-- Acciones -->
								<div class="col-span-1 flex justify-center">
									<Menu
										as="div"
										class="relative"
										v-if="
											hasPermissionUser(
												PermissionUser.UpdateUsersCompanies,
												EntityType.CompanyType
											) ||
											hasPermissionUser(
												PermissionUser.DeleteUsersCompanies,
												EntityType.CompanyType
											) ||
											(hasPermissionUser(
												PermissionUser.RestoreUsersCompanies,
												EntityType.CompanyType
											) &&
												user.deletedAt)
										"
									>
										<Float
											strategy="fixed"
											placement="bottom-end"
											:offset="4"
											enter="transition ease-out duration-100"
											enter-from="transform opacity-0 scale-95"
											enter-to="transform opacity-100 scale-100"
											leave="transition ease-in duration-75"
											leave-from="transform opacity-100 scale-100"
											leave-to="transform opacity-0 scale-95"
										>
											<MenuButton
												class="p-2 rounded-lg hover:bg-gray-100 transition-colors"
											>
												<EllipsisVerticalIcon class="w-5 h-5 text-gray-500" />
											</MenuButton>

											<MenuItems
												class="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-xl bg-white shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none border border-gray-200 z-50"
											>
												<div class="p-1">
													<MenuItem
														v-if="
															!user.deletedAt &&
															hasPermissionUser(
																PermissionUser.UpdateUsersCompanies,
																EntityType.CompanyType
															)
														"
														v-slot="{ active }"
													>
														<RouterLink
															:to="{
																name: toEditUser.name,
																params: { id: user.id },
															}"
															:class="[
																'flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors',
																active ?
																	'bg-blue-50 text-blue-700'
																:	'text-gray-700',
															]"
														>
															<PencilSquareIcon class="w-5 h-5" />
															Editar usuario
														</RouterLink>
													</MenuItem>

													<MenuItem
														v-if="
															!user.deletedAt &&
															hasPermissionUser(
																PermissionUser.UpdateUsersCompanies,
																EntityType.CompanyType
															)
														"
														v-slot="{ active }"
													>
														<RouterLink
															:to="{
																name: toPermissionsUser.name,
																params: { id: user.id },
															}"
															:class="[
																'flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors',
																active ?
																	'bg-purple-50 text-purple-700'
																:	'text-gray-700',
															]"
														>
															<Icon
																icon="mdi:shield-key"
																class="w-5 h-5"
															/>
															Roles y permisos
														</RouterLink>
													</MenuItem>

													<MenuItem
														v-if="
															!user.deletedAt &&
															user.email &&
															!user.emailVerifiedAt &&
															hasPermissionUser(
																PermissionUser.UpdateUsersCompanies,
																EntityType.CompanyType
															)
														"
														v-slot="{ active }"
													>
														<button
															@click.stop.prevent="
																handleSendVerifyEmail(user.id.toString())
															"
															:class="[
																'w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors',
																active ?
																	'bg-blue-50 text-blue-700'
																:	'text-gray-700',
															]"
														>
															<EnvelopeIcon class="w-5 h-5" />
															Verificar correo
														</button>
													</MenuItem>

													<MenuItem
														v-if="
															!user.deletedAt &&
															hasPermissionUser(
																PermissionUser.DeleteUsersCompanies,
																EntityType.CompanyType
															)
														"
														v-slot="{ active }"
													>
														<button
															@click.stop.prevent="
																emits('deactivateUser', user.id.toString())
															"
															:class="[
																'w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors',
																active ?
																	'bg-red-50 text-red-700'
																:	'text-gray-700',
															]"
														>
															<Icon
																icon="mdi:account-off"
																class="w-5 h-5"
															/>
															Desactivar usuario
														</button>
													</MenuItem>

													<MenuItem
														v-if="
															user.deletedAt &&
															hasPermissionUser(
																PermissionUser.RestoreUsersCompanies,
																EntityType.CompanyType
															)
														"
														v-slot="{ active }"
													>
														<button
															@click.stop.prevent="
																emits('reactivateUser', user.id.toString())
															"
															:class="[
																'w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors',
																active ?
																	'bg-green-50 text-green-700'
																:	'text-gray-700',
															]"
														>
															<Icon
																icon="mdi:account-check"
																class="w-5 h-5"
															/>
															Reactivar usuario
														</button>
													</MenuItem>
												</div>
											</MenuItems>
										</Float>
									</Menu>
								</div>
							</div>
						</template>

						<!-- Mensaje cuando no hay datos en desktop -->
						<template v-else>
							<div class="px-6 py-12 text-center">
								<Icon
									icon="mdi:account-off-outline"
									class="w-16 h-16 mx-auto text-gray-300 mb-4"
								/>
								<h3 class="text-lg font-semibold text-gray-900 mb-2">
									No hay usuarios
								</h3>
								<p class="text-sm text-gray-500">
									No se encontraron usuarios que coincidan con los filtros
									aplicados.
								</p>
							</div>
						</template>
					</div>
				</div>
			</section>
		</div>

		<!-- Paginación moderna integrada -->
		<div
			v-if="pagination?.meta?.lastPage && pagination.meta.lastPage > 1"
			class="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-gray-100 bg-gray-50/50 rounded-b-xl px-6 py-4"
		>
			<!-- Información de registros -->
			<div class="flex items-center gap-4 text-sm text-gray-600">
				<span v-if="pagination.meta.total">
					Mostrando
					<span class="font-semibold text-gray-900">{{
						pagination.meta.from
					}}</span>
					-
					<span class="font-semibold text-gray-900">{{
						pagination.meta.to
					}}</span>
					de
					<span class="font-semibold text-gray-900">{{
						pagination.meta.total
					}}</span>
					usuarios
				</span>
				<span
					v-else
					class="text-gray-500"
				>
					No se encontraron usuarios
				</span>
			</div>

			<!-- Controles de paginación -->
			<div class="flex items-center gap-2">
				<!-- Botón anterior -->
				<button
					:disabled="pagination.meta.currentPage === 1"
					@click="changePage(pagination.meta.currentPage - 1)"
					:class="[
						'flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
						pagination.meta.currentPage === 1 ?
							'text-gray-400 cursor-not-allowed bg-gray-100'
						:	'text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 shadow-sm',
					]"
				>
					<svg
						class="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M15 19l-7-7 7-7"
						/>
					</svg>
					<span class="hidden sm:inline">Anterior</span>
				</button>

				<!-- Números de página -->
				<div class="flex items-center gap-1">
					<template
						v-for="pageNum in getVisiblePages()"
						:key="pageNum"
					>
						<button
							v-if="pageNum !== '...'"
							@click="changePage(Number(pageNum))"
							:class="[
								'min-w-[2.5rem] px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
								pagination.meta.currentPage === pageNum ?
									'bg-blue-600 text-white shadow-md hover:bg-blue-700'
								:	'text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300',
							]"
						>
							{{ pageNum }}
						</button>
						<span
							v-else
							class="px-2 py-2 text-gray-400"
							>...</span
						>
					</template>
				</div>

				<!-- Botón siguiente -->
				<button
					:disabled="pagination.meta.currentPage === pagination.meta.lastPage"
					@click="changePage(pagination.meta.currentPage + 1)"
					:class="[
						'flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
						pagination.meta.currentPage === pagination.meta.lastPage ?
							'text-gray-400 cursor-not-allowed bg-gray-100'
						:	'text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 shadow-sm',
					]"
				>
					<span class="hidden sm:inline">Siguiente</span>
					<svg
						class="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M9 5l7 7-7 7"
						/>
					</svg>
				</button>
			</div>
		</div>
	</div>
</template>

<style scoped>
	.line-clamp-2 {
		display: -webkit-box;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	.paginate-button {
		position: relative;
		display: inline-flex;
		align-items: center;
		border: 1px solid #d1d5db;
		background-color: white;
		padding: 0.5rem;
		font-size: 0.875rem;
		font-weight: 500;
		color: #6b7280;
		transition: all 0.2s ease-in-out;
	}

	.paginate-button:first-child {
		border-top-left-radius: 0.375rem;
		border-bottom-left-radius: 0.375rem;
	}

	.paginate-button:last-child {
		border-top-right-radius: 0.375rem;
		border-bottom-right-radius: 0.375rem;
	}

	.paginate-button:hover {
		background-color: #f9fafb;
	}

	.paginate-button:focus {
		z-index: 20;
	}

	/* Mejoras adicionales para móvil */
	@media (max-width: 1024px) {
		.mobile-card-shadow {
			box-shadow:
				0 1px 3px 0 rgba(0, 0, 0, 0.1),
				0 1px 2px 0 rgba(0, 0, 0, 0.06);
		}

		.mobile-card-shadow:hover {
			box-shadow:
				0 10px 15px -3px rgba(0, 0, 0, 0.1),
				0 4px 6px -2px rgba(0, 0, 0, 0.05);
		}
	}
</style>
